/**
 * Reseller list table (TMMPDM-0015 / 0022): Name · Slug · Status · Standing · Tenants · Created.
 * TMMPDM-0035: sticky column headers; a reseller link carries the list view as its `from` state.
 */
import type { ReactNode } from 'react';
import {
  Box,
  Chip,
  Link,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useCopy } from '@/hooks/useCopy';
import { RowActionsMenu, hasEditableFields } from '@/components/page';
import { stickyTableContainerSx } from '@/components/ui';
import { tenantStatusLabel } from '@/domain/registry';
import { StatusChip } from '@/status';
import type { ReturnState } from '@/hooks/useReturnTo';
import type { ResellerDto } from '@/api/tmm/types';
import { StandingChip } from '../resellers/StandingChip';
import { PartnerSellable } from './PartnerSellable';
import { formatInstant } from '@/format';

/** `standing` is set on list rows (tmm-api 0077); a row without it shows `—` (TMMPDM-0023). */
function renderStanding(reseller: ResellerDto) {
  const state = reseller.standing?.state;
  return state ? <StandingChip state={state} /> : '—';
}

interface PartnersTableProps {
  resellers: ResellerDto[];
  loading: boolean;
  /** The `EmptyState` shown in the one full-width cell when there are no rows. */
  empty: ReactNode;
  /** The list as it is now, carried to the reseller's "Resellers" breadcrumb. */
  linkState?: ReturnState;
  onEditDetails: (reseller: ResellerDto) => void;
}

export function PartnersTable({
  resellers,
  loading,
  empty,
  linkState,
  onEditDetails,
}: PartnersTableProps) {
  const { t } = useCopy();
  const { i18n } = useTranslation();

  return (
    <TableContainer
      component={Paper}
      variant="outlined"
      aria-busy={loading ? 'true' : 'false'}
      sx={stickyTableContainerSx}
    >
      <Table aria-label={t('page.partners.title', 'Partners')} size="small" stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 600 }}>{t('partners.singular', 'Partner')}</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>{t('resellers.col.status', 'Status')}</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>
              {t('resellers.col.standing', 'Standing')}
            </TableCell>
            <TableCell sx={{ fontWeight: 600 }} align="right">
              {t('partners.canSell', 'Can sell')}
            </TableCell>
            <TableCell sx={{ fontWeight: 600 }}>{t('partners.house.chip', 'House')}</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>{t('resellers.col.created', 'Created')}</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>
              {t('resellerCards.col.actions', 'Actions')}
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {loading ? (
            <TableRow>
              <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                <Typography variant="body2" color="text.secondary">
                  {t('common.loading', 'Loading')}
                </Typography>
              </TableCell>
            </TableRow>
          ) : resellers.length === 0 ? (
            <TableRow>
              <TableCell colSpan={7}>{empty}</TableCell>
            </TableRow>
          ) : (
            resellers.map((reseller) => (
              <TableRow key={reseller.id} hover>
                <TableCell>
                  <Link
                    component={RouterLink}
                    to={`/partners/${reseller.id}`}
                    state={linkState}
                    variant="body2"
                    underline="hover"
                  >
                    {reseller.name}
                  </Link>
                  {reseller.tenant && reseller.tenant.name !== reseller.name ? (
                    <Typography variant="caption" sx={{ display: 'block' }}>
                      {reseller.tenant.name} ·{' '}
                      <Box component="span" sx={(theme) => theme.typography.mono}>
                        {reseller.tenant.slug}
                      </Box>
                    </Typography>
                  ) : null}
                </TableCell>
                <TableCell>
                  <StatusChip
                    status={reseller.status}
                    label={tenantStatusLabel(reseller.status, t)}
                  />
                </TableCell>
                <TableCell>{renderStanding(reseller)}</TableCell>
                <TableCell align="right">
                  <PartnerSellable tenant={reseller.tenant} />
                </TableCell>
                <TableCell>
                  {reseller.platformOwned ? (
                    <Chip size="small" label={t('partners.house.chip', 'House')} />
                  ) : (
                    '—'
                  )}
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {reseller.createdAt
                      ? formatInstant(reseller.createdAt, i18n.language, 'date')
                      : '—'}
                  </Typography>
                </TableCell>
                <TableCell>
                  <RowActionsMenu
                    name={reseller.name}
                    items={
                      hasEditableFields(reseller.editableFields)
                        ? [
                            {
                              key: 'details',
                              label: t('components.editDetails.menu', 'Edit details…'),
                              onSelect: () => onEditDetails(reseller),
                            },
                          ]
                        : []
                    }
                  />
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
